import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getIdMap } from 'ontraccr-common/lib/Common';

import { getBucketTemplateTableColumns } from '../../forms/formHelpers';
import {
  getABWorkReportingData,
  convertTableColumnsToArray,
  getStatus,
  isClosedOrHistoricalBucket,
} from './BoardCardABWorkReportingHelpers';
import { getColumns } from '../../forms/FormBuilder/FormFields/BucketTablePreview';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import { getWorkReportEntries, getABWorkReportingDisplayFields } from '../../abWorkReporting/state/abWorkReporting.actions';
import useToggle from '../../common/hooks/useToggle';
import ABWorkReportingEntryCreator from './ABWorkReportingEntryCreator';
import BoardCardABWorkReportingHeader from './BoardCardABWorkReportingHeader';
import BoardCardABWorkReportingItemDetails from './BoardCardABWorkReportingItemDetails';
import BoardCardABWorkReportingTable from './BoardCardABWorkReportingTable';
import BoardCardABWorkReportingSettingsDrawer from './BoardCardABWorkReportingSettingsDrawer';
import { updateBucket, updateBucketStatuses, getBucketsCustomData } from '../../buckets/state/buckets.actions';

export default function BoardCardABWorkReporting({
  cardId,
  data,
}) {
  const dispatch = useDispatch();

  const settings = useSelector((state) => state.settings.company);
  const buckets = useSelector((state) => state.buckets.buckets);
  const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);
  const statuses = useSelector((state) => state.statuses.statuses);
  const abWorkReportingEntries = useSelector((state) => state.abWorkReporting.entries);
  const userDisplayFields = useSelector((state) => state.abWorkReporting.displayFields);

  useEffect(() => {
    dispatch(getABWorkReportingDisplayFields());
  }, []);

  const bucketTemplateMap = getIdMap(bucketTemplates);
  const bucketMap = useMemo(() => getIdMap(buckets), [buckets]);
  const abWorkReportingEntryMap = abWorkReportingEntries.reduce((acc, entry) => {
    if (!acc[entry.bidItemId]) {
      acc[entry.bidItemId] = 0;
    }
    acc[entry.bidItemId] += entry.quantity;
    return acc;
  }, {});

  const {
    toggle: toggleABWorkReportingEntryCreator,
    isToggled: isABWorkReportingEntryCreatorVisible,
  } = useToggle();

  const {
    toggle: toggleABWorkReportingSettingsDrawer,
    isToggled: isABWorkReportingSettingsDrawerVisible,
  } = useToggle();

  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [selectedBidItems, setSelectedBidItems] = useState([]);

  const [prevSelectedItem, setPrevSelectedItem] = useState(null);
  const [prevSelectedSegment, setPrevSelectedSegment] = useState(null);
  const [prevCardId, setPrevCardId] = useState(null);
  const [prevData, setPrevData] = useState(null);

  const [selectedBidItemKeys, setSelectedBidItemKeys] = useState([]);

  const {
    settings: {
      abWorkReportingMapping: {
        itemId,
        segmentId,
        bidItemId,
        authorizedQuantityField,
        dateField,
      } = {},
    } = {},
  } = settings;

  useEffect(() => {
    const relevantTemplateIds = [itemId, segmentId, bidItemId].filter(Boolean);
    if (!relevantTemplateIds.length) return;
    const relevantTemplateSet = new Set(relevantTemplateIds);
    const idsNeedingCustomData = buckets
      .filter((bucket) => relevantTemplateSet.has(bucket.bucketTemplateId) && !bucket.customData)
      .map((bucket) => bucket.id);
    if (idsNeedingCustomData.length) dispatch(getBucketsCustomData(idsNeedingCustomData));
  }, [buckets, itemId, segmentId, bidItemId]);

  const {
    itemDisplayFields,
    segmentDisplayFields,
    bidItemDisplayFields,
  } = userDisplayFields || {};

  const itemDisplayFieldOptions = convertTableColumnsToArray(
    getBucketTemplateTableColumns({
      data: bucketTemplateMap[itemId],
      additionalBaseOptions: {
        numberOfSegments: 'Segment Count',
        numberOfBidItems: 'Bid Item Count',
      },
    }),
    itemDisplayFields,
  );
  const segmentDisplayFieldOptions = convertTableColumnsToArray(
    getBucketTemplateTableColumns({
      data: bucketTemplateMap[segmentId],
      additionalBaseOptions: {
        numberOfBidItems: 'Bid Item Count',
      },
    }),
    segmentDisplayFields,
  );
  const bidItemDisplayFieldOptions = convertTableColumnsToArray(
    getBucketTemplateTableColumns({
      data: bucketTemplateMap[bidItemId],
      additionalBaseOptions: {
        repQty: 'Bid Item Rep Qty',
        number: 'Task Number',
      },
    }),
    bidItemDisplayFields,
  );

  const {
    items,
  } = getABWorkReportingData({
    data,
    itemId,
    segmentId,
    bidItemId,
    bucketMap,
    statuses,
    abWorkReportingEntryMap,
  });

  if (prevCardId !== cardId && cardId) {
    dispatch(getWorkReportEntries(cardId));
    setPrevCardId(cardId);
  }

  if (prevSelectedItem?.id !== selectedItem?.id) {
    if (selectedSegment) setSelectedSegment();
    if (selectedBidItemKeys?.length) setSelectedBidItemKeys([]);
    setPrevSelectedItem(selectedItem);
  }

  if (prevSelectedSegment?.id !== selectedSegment?.id) {
    if (selectedBidItemKeys?.length) setSelectedBidItemKeys([]);
    setPrevSelectedSegment(selectedSegment);
  }

  if (
    prevData?.buckets !== buckets
    || prevData?.abWorkReportingEntries !== abWorkReportingEntries
    || prevData?.statuses !== statuses
    || prevData?.data !== data
  ) {
    setPrevData({
      buckets,
      abWorkReportingEntries,
      statuses,
      data,
    });

    const relevantItem = items.find((item) => item.id === selectedItem?.id);
    const relevantSegment = relevantItem?.segments?.find((segment) => segment.id === selectedSegment?.id);

    setSelectedItem((prev) => relevantItem || prev);
    setSelectedSegment((prev) => relevantSegment || prev);
  }

  if (!selectedItem && items?.length) {
    setSelectedItem(items[0]);
  }

  if (!selectedSegment && selectedItem?.segments?.length) {
    setSelectedSegment(selectedItem.segments[0]);
  }

  const itemMap = getIdMap(items);
  const onItemStatusChange = (isBidItem = false) => (id, key, value) => {
    const relevantItem = !isBidItem
      ? itemMap[id]
      : selectedItem?.bidItems?.find((bidItem) => bidItem.id === id);
    if (key !== 'statusId' || !relevantItem) return;

    const newStatus = getStatus({ bucketTemplateId: relevantItem.bucketTemplateId, statusId: value }, statuses);

    // If status is not closed, update the bucket status
    if (newStatus !== 'Closed') {
      dispatch(updateBucket(id, { statusId: value }));
      return;
    }

    if (newStatus === 'Closed' && isBidItem) {
      const payload = [{ id, statusId: value }];
      const allBidItemsDone = selectedItem.bidItems.every((b) => (
        b.id === id || isClosedOrHistoricalBucket(b, statuses)
      ));

      // If all bid items are closed or historical, update the parent item status to closed
      if (allBidItemsDone) {
        const itemStatuses = statuses[itemId];
        const itemClosedStatus = Object.values(itemStatuses || {}).find((s) => s.status === 'Closed');
        if (itemClosedStatus) {
          payload.push({ id: selectedItem.id, statusId: itemClosedStatus.id });
        }
      }

      dispatch(updateBucketStatuses(payload));
      return;
    }

    // Otherwise we update the child bid items to closed
    const payload = [{ id, statusId: value }];
    const relevantBidItemStatuses = statuses[bidItemId];
    const closedStatus = Object.values(relevantBidItemStatuses).find((status) => status.status === 'Closed');

    if (closedStatus) {
      relevantItem.bidItems.forEach((bidItem) => {
        payload.push({ id: bidItem.id, statusId: closedStatus.id });
      });
    }
    dispatch(updateBucketStatuses(payload));
  }

  const itemColumns = getColumns({
    columns: itemDisplayFieldOptions,
    isDisplay: true,
    requiredColumns: false,
    preventEdits: true,
    relevantBucketTemplate: bucketTemplateMap[itemId],
    responding: false,
    includeLineNumber: false,
    additionalColumnMap: {
      numberOfSegments: {
        title: 'Segment Count',
        dataIndex: 'numberOfSegments',
      },
      numberOfBidItems: {
        title: 'Bid Item Count',
        dataIndex: 'numberOfBidItems',
      },
    },
    shouldDisableHistorical: true,
    shouldAllowStatusUpdates: (record) => getStatus(record, statuses) !== 'Historical',
    relevantStatuses: statuses[itemId],
    onValueUpdated: onItemStatusChange(false),
  });

  const segmentColumns = getColumns({
    columns: segmentDisplayFieldOptions,
    isDisplay: true,
    requiredColumns: false,
    preventEdits: true,
    relevantBucketTemplate: bucketTemplateMap[segmentId],
    responding: false,
    includeLineNumber: false,
    additionalColumnMap: {
      numberOfBidItems: {
        title: 'Bid Item Count',
        dataIndex: 'numberOfBidItems',
      },
    },
    relevantStatuses: statuses[segmentId],
  });

  const bidItemColumns = getColumns({
    columns: bidItemDisplayFieldOptions,
    isDisplay: true,
    requiredColumns: false,
    preventEdits: true,
    relevantBucketTemplate: bucketTemplateMap[bidItemId],
    responding: false,
    includeLineNumber: false,
    relevantStatuses: statuses[bidItemId],
    additionalColumnMap: {
      repQty: {
        title: 'Rep Qty',
        dataIndex: 'repQty',
        width: 75,
      },
      number: {
        title: 'Task Number',
        dataIndex: 'number',
        width: 75,
      },
    },
    shouldDisableHistorical: true,
    shouldAllowStatusUpdates: (record) => (
      getStatus(record?.parentItem, statuses) !== 'Closed'
      && getStatus(record, statuses) !== 'Historical'
    ),
    onValueUpdated: onItemStatusChange(true),
  });

  const onCreateWorkReport = ({ key }) => {
    switch (key) {
      case 'item':
        setSelectedBidItems(selectedItem.bidItems);
        toggleABWorkReportingEntryCreator();
        break;
      case 'segment':
        setSelectedBidItems(selectedSegment.bidItems);
        toggleABWorkReportingEntryCreator();
        break;
      case 'bidItems': {
        const bidItemSet = new Set(selectedBidItemKeys);
        const bidItems = selectedSegment?.bidItems || selectedItem?.bidItems || [];
        setSelectedBidItems(bidItems.filter((bidItem) => bidItemSet.has(bidItem.id)));
        toggleABWorkReportingEntryCreator();
        break;
      }
      default:
        break;
    }
  };

  const onCancel = () => {
    setSelectedBidItems([]);
    toggleABWorkReportingEntryCreator();
  };

  if (!itemId || !segmentId || !bidItemId) {
    return (
      <div className="ab-work-reporting-container-no-data">
        <Row justify="center">
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            Please set up the AB Work Reporting mapping in the company settings.
          </div>
        </Row>
        <Row justify="center" style={{ marginTop: 5 }}>
          <OnTraccrButton title="Settings" onClick={toggleABWorkReportingSettingsDrawer} />
        </Row>
        <BoardCardABWorkReportingSettingsDrawer
          visible={isABWorkReportingSettingsDrawerVisible}
          onClose={toggleABWorkReportingSettingsDrawer}
        />
      </div>
    );
  }

  return (
    <div>
      <BoardCardABWorkReportingHeader
        onCreateWorkReport={onCreateWorkReport}
        selectedItem={selectedItem}
        selectedSegment={selectedSegment}
        selectedBidItemKeys={selectedBidItemKeys}
        onSettingsClick={toggleABWorkReportingSettingsDrawer}
      />
      <Row className="ab-work-reporting-container" style={{ overflow: 'auto', maxHeight: 500, width: 'fit-content' }}>
        <BoardCardABWorkReportingTable
          dataSource={items}
          columns={itemColumns}
          selectedEntity={selectedItem}
          setSelectedEntity={setSelectedItem}
          onRow={(record) => ({
            onClick: () => {
              setSelectedItem(record);
              setSelectedSegment();
            },
            className: 'timecard-entry-row'
          })}
        />
      </Row>
      <BoardCardABWorkReportingItemDetails item={selectedItem} itemColumns={itemColumns} />
      <Row className="ab-work-reporting-container">
        <Col span={6} style={{ overflow: 'auto', borderRight: '1px solid lightgray', paddingRight: 10 }}>
          <BoardCardABWorkReportingTable
            dataSource={selectedItem?.segments || []}
            columns={segmentColumns}
            selectedEntity={selectedSegment}
            setSelectedEntity={setSelectedSegment}
            onRow={(record) => ({
              onClick: () => setSelectedSegment(record),
              className: 'timecard-entry-row'
            })}
          />
        </Col>
        <Col span={18} style={{ overflow: 'auto', paddingLeft: 10 }}>
          <BoardCardABWorkReportingTable
            dataSource={selectedSegment?.bidItems || selectedItem?.bidItems || []}
            columns={bidItemColumns}
            rowSelection={{
              selectedRowKeys: selectedBidItemKeys,
              onChange: setSelectedBidItemKeys,
              hideSelectAll: false,
              onSelect: () => {},
              getCheckboxProps: (record) => ({
                disabled: isClosedOrHistoricalBucket(record, statuses),
                className: 'ontraccr-checkbox',
              }),
            }}
            onRow={(record) => ({
              onClick: () => {
                if (selectedBidItemKeys.includes(record.id)) {
                  setSelectedBidItemKeys(selectedBidItemKeys.filter((key) => key !== record.id));
                } else {
                  setSelectedBidItemKeys([...selectedBidItemKeys, record.id]);
                }
              },
              className: 'timecard-entry-row'
            })}
          />
        </Col>
      </Row>
      <ABWorkReportingEntryCreator
        visible={isABWorkReportingEntryCreatorVisible}
        onCancel={onCancel}
        cardId={cardId}
        itemId={itemId}
        bidItems={selectedBidItems}
        bidItemColumns={bidItemColumns}
        selectedItem={selectedItem}
        authorizedQuantityField={authorizedQuantityField}
        dateField={dateField}
      />
      <BoardCardABWorkReportingSettingsDrawer
        visible={isABWorkReportingSettingsDrawerVisible}
        onClose={toggleABWorkReportingSettingsDrawer}
      />
    </div>
  );
}
